import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import s from "../modules/TrainerCreate.module.css";
import { useT, useI18nReady } from "./TextProvider";

// ===== helpers: auth
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") || s.getItem("auth:token") || s.getItem("auth:key") ||
    s.getItem("auth:jwt") || s.getItem("auth:auth_token") || null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}

function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME;
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

// ===== API endpoints
const FANLAR_ENDPOINT =
  process.env.REACT_APP_FANLAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/fanlar/";

export default function NewSub() {
  const t = useT();
  const ready = useI18nReady();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    description: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState("");

  const user = useMemo(() => getStoredUser(), []);

  // ===== Image preview
  useEffect(() => {
    if (!imgFile) { setImgPreview(""); return; }
    const url = URL.createObjectURL(imgFile);
    setImgPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imgFile]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function onImgChange(e) {
    const file = e.target.files?.[0];
    if (file) setImgFile(file);
  }

  function validateForm() {
    if (!form.name.trim()) return "Fanning nomini kiriting";
    if (!form.description.trim()) return "Tavsifni kiriting";
    if (!imgFile) return "Rasmni yuklang";
    return "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    const v = validateForm();
    if (v) { setError(v); return; }

    try {
      setLoading(true);
      const token = getAnyTokenFromStorage();

      // Используем FormData для multipart/form-data
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("description", form.description.trim());
      if (user?.id) {
        fd.append("user", user.id);
      }
      fd.append("img", imgFile);

      const res = await fetch(FANLAR_ENDPOINT, {
        method: "POST",
        headers: {
          ...(token ? makeAuthHeader(token) : {}),
          // НЕ устанавливаем Content-Type - браузер установит правильный с boundary
        },
        body: fd,
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const serverMsg = (typeof data === "string" && data) || data?.detail || data?.error || data?.message ||
          (data && Array.isArray(Object.values(data)[0]) ? Object.values(data)[0] : null) ||
          "Fanni yaratishda xatolik";
        throw new Error(Array.isArray(serverMsg) ? serverMsg.join(" ") : String(serverMsg));
      }

      setSuccess("Fan muvaffaqiyatli yaratildi!");
      setForm({ name: "", description: "" });
      setImgFile(null);
      setImgPreview("");

      setTimeout(() => {
        navigate("/MeningFanlarim", { replace: true });
      }, 1500);
    } catch (err) {
      setError(err?.message || "Xatolik yuz berdi");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={s.wrapper}>
      <div className={s.card}>
        <h1 className={s.title}>Yangi Fan Yaratish</h1>

        <form className={s.form} onSubmit={handleSubmit}>
          {/* Fanning nomi */}
          <div className={s.field}>
            <label className={s.label}>Fanning nomi *</label>
            <input
              className={s.input}
              type="text"
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Masalan: Web dasturlash, Python, QA Testing"
              required
            />
          </div>

          {/* Tavsif */}
          <div className={s.field}>
            <label className={s.label}>Tavsif *</label>
            <textarea
              className={s.textarea}
              name="description"
              value={form.description}
              onChange={onChange}
              placeholder="Fanning qisqa tavsifini kiriting"
              rows={4}
              required
            />
          </div>

          {/* Rasm fayli */}
          <div className={s.field}>
            <label className={s.label}>Rasmni yuklash *</label>
            <p style={{ fontSize: "0.9em", color: "var(--text-secondary)", marginBottom: "8px" }}>
              💡 Rasmlar manzilini quyidagilardan tanlang: flaticon.com
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={onImgChange}
              required
            />
            {imgPreview && (
              <div className={s.previewBox}>
                <img src={imgPreview} alt="preview" className={s.previewImg} />
              </div>
            )}
          </div>

          {/* Xatolik va muvaffaqiyat xabarlari */}
          {error && <div className={s.error}>{error}</div>}
          {success && <div className={s.success}>{success}</div>}

          {/* Tugmalar */}
          <div className={s.actions}>
            <button className={s.submit} type="submit" disabled={loading}>
              {loading ? "Saqlayapmiz…" : "Fanni Yaratish"}
            </button>
            <button
              type="button"
              className={s.secondary}
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Bekor Qilish
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
